// Cloudflare Turnstile — anti-bot CAPTCHA for the UNAUTHENTICATED auth forms only
// (sign up, forgot password, and sign in). It guards the email-sending surfaces so a
// bot flood can't burn our Resend quota or get the pointbunny.com sending domain flagged.
//
// The token is verified SERVER-SIDE by Supabase Auth (we pass `options.captchaToken`
// to signUp / signInWithPassword / resetPasswordForEmail). A client-only check would be
// trivially bypassable, so Supabase's native CAPTCHA support is the real gate - this
// module only renders the widget and hands the token to the controller.
//
// Config: the PUBLIC site key comes from `process.env.TURNSTILE_SITE_KEY` (Parcel inlines
// it at build time, same as the Supabase keys). The matching SECRET key is pasted into
// Supabase -> Authentication -> Settings, NEVER shipped to the client.
//
// Graceful no-op when unconfigured: if the site key is blank (Kelly hasn't created the
// Cloudflare keys yet), `captchaEnabled()` is false, no widget renders, no script loads,
// and the auth forms work exactly as before. Turn it on by setting TURNSTILE_SITE_KEY
// AND enabling CAPTCHA in Supabase at the same time (otherwise the two sides disagree).
//
// Cloudflare test keys (always pass / always block) for local dev:
//   site `1x00000000000000000000AA` + secret `1x0000000000000000000000000000000AA` => always passes
//   site `2x00000000000000000000AB` + secret `2x0000000000000000000000000000000AA` => always blocks

const SITE_KEY   = process.env.TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let _scriptPromise = null;
const _widgets = new Map(); // containerId -> Turnstile widget id

export const captchaEnabled = () => Boolean(SITE_KEY);

function _loadScript() {
  if (!SITE_KEY) return Promise.resolve(false);
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve) => {
    if (window.turnstile) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// Collapse the widget once it holds a valid token, or re-show it when the token is
// gone. This is PURELY VISUAL - getResponse() still returns the token from a hidden
// widget, so the anti-bot protection is unaffected. The invariant we must keep: the
// widget is hidden ONLY while a valid token exists, so a re-challenge (after reset /
// expiry / error) is always visible and can never silently block the user.
function _setSolvedUI(containerId, solved) {
  const el = document.getElementById(containerId);
  if (el) el.classList.toggle('auth-captcha--solved', solved);
}

// Render a widget into a container (idempotent per container). Safe to call when the
// container is visible - rendering into a display:none element gives a 0-width widget,
// so callers render login at show-time and signup/forgot lazily on first reveal.
export async function renderCaptcha(containerId) {
  if (!SITE_KEY) return;
  const ok = await _loadScript();
  if (!ok || !window.turnstile) return;
  const el = document.getElementById(containerId);
  if (!el || _widgets.has(containerId)) return;
  try {
    const id = window.turnstile.render(el, {
      sitekey: SITE_KEY,
      theme: 'auto',
      size: 'flexible',
      // Hide once solved (after a brief beat so the "Success" tick is seen); re-show
      // whenever the token is lost so any re-challenge stays visible.
      callback: () => setTimeout(() => _setSolvedUI(containerId, true), 600),
      'expired-callback': () => _setSolvedUI(containerId, false),
      'error-callback': () => _setSolvedUI(containerId, false),
    });
    _widgets.set(containerId, id);
  } catch {
    // Already rendered or a transient render error - leave the form usable.
  }
}

// Current token for a container, or undefined if disabled / not solved yet.
export function getToken(containerId) {
  if (!SITE_KEY || !window.turnstile) return undefined;
  const id = _widgets.get(containerId);
  if (id === undefined) return undefined;
  return window.turnstile.getResponse(id) || undefined;
}

// Turnstile tokens are single-use (Supabase consumes them on the auth call), so reset
// after every submit attempt to get a fresh token for any retry.
export function resetCaptcha(containerId) {
  if (!SITE_KEY || !window.turnstile) return;
  const id = _widgets.get(containerId);
  if (id !== undefined) {
    try { window.turnstile.reset(id); } catch { /* widget gone - ignore */ }
    // Reset clears the token, so re-show the widget; a fresh solve re-hides it.
    _setSolvedUI(containerId, false);
  }
}

// On-demand token for IN-APP re-authentication (manager override, time clock) where a
// persistent inline widget doesn't fit. Because CAPTCHA enforcement in Supabase is
// PROJECT-WIDE, even these post-login signInWithPassword calls need a token - so we
// render a managed Turnstile in a transient, centered overlay: trusted users solve
// invisibly (resolves in ~a second), suspicious users get an interactive challenge in
// the floating box. Resolves `undefined` when CAPTCHA is disabled (no token needed) or
// on error/timeout (the auth call then just fails its own captcha check and the user
// can retry). Single-use - call once per attempt. Styles are inline so it works in any
// bundle (e.g. the standalone time-clock page) without depending on a stylesheet.
export async function getTokenInteractive() {
  if (!SITE_KEY) return undefined;
  const ok = await _loadScript();
  if (!ok || !window.turnstile) return undefined;
  return new Promise((resolve) => {
    const holder = document.createElement('div');
    holder.style.cssText =
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2147483000;';
    document.body.appendChild(holder);
    let settled = false;
    let wid;
    const cleanup = (token) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { if (wid !== undefined) window.turnstile.remove(wid); } catch { /* ignore */ }
      holder.remove();
      resolve(token || undefined);
    };
    const timer = setTimeout(() => cleanup(undefined), 60000);
    try {
      wid = window.turnstile.render(holder, {
        sitekey: SITE_KEY,
        theme: 'auto',
        size: 'normal',
        callback: (token) => cleanup(token),
        'error-callback': () => cleanup(undefined),
        'timeout-callback': () => cleanup(undefined),
      });
    } catch {
      cleanup(undefined);
    }
  });
}
